// Recording a booking (DESIGN.md, Views, "Record a booking where it's made", and Records, `paid`;
// features/bookings-and-costs.md, "Record a booking"): how each payment was paid, in a few words, the card by
// name and never its digits; and which things the web's booking form is for. Plain, so the store, the schema
// (model.ts), the web's route (web/api.ts) and the form (web/src/BookingForm.tsx) share it without the schema's
// library.

// How it was paid ("Visa", "Mei's Visa", "Cash", "Bank transfer", "Citi Prestige points"): a few words.
export const MAX_METHOD = 60;

// The card by name, never its digits (DESIGN.md, Records, `paid`; the founder, 2026-10-31, for ChatGPT's rule
// against collecting card data): 4 or more digits in a row, a card's last 4 as much as its whole number.
const DIGITS = /\d{4}/;
export const digitsIn = (text: string): boolean => DIGITS.test(text);
export const METHOD_REFUSED = `how it was paid names the card, never its digits: "Visa", "Mei's Visa", "Cash", "Bank transfer" or "Citi Prestige points" (4 or more digits in a row are refused)`;
// The same, in the web's words (the booking form, Add a payment, and their routes).
export const NAME_THE_CARD = "Name the card, never its digits: Visa, Mei's Visa, Cash.";

// Every method a thing's payments say, paid and charged later alike.
type Paying = {
  paid?: readonly { method?: string | undefined }[] | undefined;
  charges?: readonly { method?: string | undefined }[] | undefined;
};
export const methodsOf = (fields: Paying | undefined): string[] =>
  [...(fields?.paid ?? []), ...(fields?.charges ?? [])].flatMap((p) => (p.method ? [p.method] : []));

// A new or changed method with digits in it, which is refused; null when there's none. One the thing already has
// (in paid or charges) is kept as it is: an old "Mastercard 8682", from before the rule, survives an edit beside
// it, a payment added next to it, and its charge moved into paid.
export function digitsAdded(next: Paying, before: Paying | undefined): string | null {
  const kept = new Set(methodsOf(before));
  return methodsOf(next).find((m) => digitsIn(m) && !kept.has(m)) ?? null;
}

// The form's quick picks for how it was paid; anything else is the payer's own few words.
export const METHOD_PICKS = ["Card", "Cash", "Points", "Bank transfer"] as const;

// What the booking form takes a thing as: a stay, a journey, an option of a choice (a hotel, a way there, a
// restaurant for one night), or a place that needs booking or is booked. Anything else (a to-do, an info, a
// goal, a choice itself, a place that's walked into) is the AI's to book. Null when it's one of those, else
// why not, in words for the person refused.
export function bookingRefused(
  item: { type: string; status: string; booking_needed?: boolean | undefined },
  parentType: string | undefined,
): string | null {
  const option = parentType === "choice";
  const kind =
    item.type === "stay" ||
    item.type === "move" ||
    (option && item.type === "place") ||
    (item.type === "place" && (item.booking_needed === true || item.status === "booked"));
  if (!kind)
    return "Only a stay, a journey, a choice's option or a place that needs booking is booked on the web; ask your AI for the rest.";
  // Cancelling, and booking again after it, stay with the AI (with the refunds that go with it).
  if (item.status === "cancelled") return "It's cancelled: ask your AI to book it again.";
  if (item.status === "dropped") return "It's been dropped from the trip: ask your AI to bring it back first.";
  return null;
}

// Whether the form edits its booking ("Edit booking": booked, or done with its booking) or marks it booked.
export const bookedAlready = (item: { status: string; booking?: unknown; paid?: readonly unknown[] | undefined }) =>
  item.status === "booked" || (item.status === "done" && Boolean(item.booking || item.paid?.length));

// The status a thing has once it's booked through the form: booked, unless it's been done already (a journey
// taken), which stays done.
export const bookedStatus = (status: string): string => (status === "done" ? "done" : "booked");
